import { DatePicker, Form, Input, InputNumber, message } from "antd";
import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import Modal from "../../../components/generic/Modal";
import MemberSelect from "../../../components/shared/MemberSelect";
import { useTenantCustomers } from "../../../hooks/useTenantCustomers";
import { parseError } from "../../../utils/shared";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../../store/user/selectors";
import { saveAppointments } from "../../../store/appointments/thunks";
import UserItem from "../../../components/shared/UserItem";

const CreateAppointmentsModal = ({
  setEditingAppointment,
  editingAppointment,
  groupedAppointments,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const user = useSelector(selectUser);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { customers, loading, selectedTenantId } = useTenantCustomers();

  const isEdit = !!editingAppointment?.id;

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const [start, end] = values.time_range;
        const hasConflict = groupedAppointments?.[
          dayjs(start).format("YYYY-MM-DD")
        ]?.some((appt) => {
          const existingStart = dayjs(appt.start_time);
          const existingEnd = dayjs(appt.end_time);

          const [newStart, newEnd] = values.time_range;

          return (
            newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart)
          );
        });

        if (hasConflict) {
          message.error("Meeting time overlaps with an existing appointment!");
          return;
        }

        setSubmitting(true);

        await dispatch(
          saveAppointments({
            ...values,
            start_time: start.toISOString(),
            end_time: end.toISOString(),
            advisor_ids: [user?.user_id],
            tenant_id: selectedTenantId,
          }),
        );

        setEditingAppointment();
      } catch (error) {
        parseError(error);
      } finally {
        setSubmitting(false);
      }
    },
    [user, groupedAppointments, selectedTenantId],
  );

  useEffect(() => {
    form.setFieldsValue({
      time_range: [
        dayjs(editingAppointment?.start_time),
        dayjs(editingAppointment?.end_time),
      ],
      title: editingAppointment?.title,
      description: editingAppointment?.description,
    });
  }, [editingAppointment]);

  return (
    <Modal
      title={"Appointment"}
      open
      onCancel={() => setEditingAppointment()}
      onOk={() => form.submit()}
      okText="Save"
      isProcessing={submitting}
      footer={isEdit ? null : undefined}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={submitting || isEdit}
      >
        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: "Please enter a meeting title" }]}
        >
          <Input placeholder="Enter meeting title" />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <Input.TextArea
            rows={3}
            placeholder="Enter meeting description"
            showCount
            maxLength={300}
          />
        </Form.Item>

        <Form.Item
          label="Select Time"
          name="time_range"
          rules={[{ required: true, message: "Please select time range" }]}
        >
          <DatePicker.RangePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            className="w-100"
            minuteStep={5}
          />
        </Form.Item>

        <Form.Item
          label="Customers"
          name="customer_ids"
          rules={[{ required: true, message: "Please Select Customers" }]}
        >
          {isEdit ? (
            <div className="flex-col">
              {editingAppointment?.customers?.map((c) => (
                <UserItem user={c} />
              ))}
            </div>
          ) : (
            <MemberSelect
              users={customers}
              placeholder="Select Customers"
              loading={loading}
              mode="tags"
            />
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateAppointmentsModal;
